import { useEffect, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft, Upload, Save } from "lucide-react";
import { toast } from "sonner";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, type PlatformSettingsOut } from "@/lib/api";

export const Route = createFileRoute("/_auth/admin/branding")({
  component: AdminBrandingPage,
});

function AdminBrandingPage() {
  const qc = useQueryClient();
  const settings = useQuery({
    queryKey: ["admin", "platform-settings"],
    queryFn: () => api.admin.getPlatformSettings(),
  });

  const [form, setForm] = useState<Partial<PlatformSettingsOut>>({});

  useEffect(() => {
    if (settings.data) setForm(settings.data);
  }, [settings.data]);

  const patch = useMutation({
    mutationFn: (body: Partial<PlatformSettingsOut>) => api.admin.patchPlatformSettings(body),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
      qc.invalidateQueries({ queryKey: ["branding"] });
      toast.success("Branding actualizado");
    },
  });

  const uploadLogo = useMutation({
    mutationFn: (file: File) => api.admin.uploadBrandingAsset("logo", file),
    onSuccess: (data) => {
      setForm((f) => ({ ...f, logo_url: data.url }));
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
      qc.invalidateQueries({ queryKey: ["branding"] });
      toast.success("Logo subido");
    },
  });

  const uploadFavicon = useMutation({
    mutationFn: (file: File) => api.admin.uploadBrandingAsset("favicon", file),
    onSuccess: (data) => {
      setForm((f) => ({ ...f, favicon_url: data.url }));
      qc.invalidateQueries({ queryKey: ["admin", "platform-settings"] });
      qc.invalidateQueries({ queryKey: ["branding"] });
      toast.success("Favicon subido");
    },
  });

  function onSubmit(e: React.FormEvent) {
    e.preventDefault();
    patch.mutate(form);
  }

  function setField<K extends keyof PlatformSettingsOut>(k: K, v: PlatformSettingsOut[K]) {
    setForm((f) => ({ ...f, [k]: v }));
  }

  return (
    <div className="px-6 py-6 max-w-[1480px] mx-auto">
      <header className="mb-6">
        <Link to="/admin" className="inline-flex items-center gap-1 text-sm text-ink-500 hover:text-ink-700 mb-2">
          <ArrowLeft size={14} /> Volver
        </Link>
        <h1 className="text-2xl font-bold text-ink-900 dark:text-white">Branding plataforma</h1>
        <p className="text-sm text-ink-500 mt-1">
          Nombre, logo, colores y emails visibles a todos los tenants. Cambios aplican tras refresh.
        </p>
      </header>

      <div className="grid lg:grid-cols-[1fr_320px] gap-4">
        <Card>
          <CardHeader>
            <CardTitle>Configuración</CardTitle>
            <CardDescription>Todos los campos son globales (singleton).</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={onSubmit} className="space-y-4">
              <div>
                <Label>Nombre de la plataforma</Label>
                <Input
                  className="mt-1"
                  value={form.brand_name ?? ""}
                  onChange={(e) => setField("brand_name", e.target.value)}
                  placeholder="TrendMonitor"
                  required
                />
              </div>

              <div>
                <Label>Nombre corto PWA (max 40 chars)</Label>
                <Input
                  className="mt-1"
                  value={form.pwa_short_name ?? ""}
                  onChange={(e) => setField("pwa_short_name", e.target.value)}
                />
              </div>

              <div>
                <Label>Color primario</Label>
                <div className="mt-1 flex gap-2 items-center">
                  <input
                    type="color"
                    value={form.primary_color ?? "#2563EB"}
                    onChange={(e) => setField("primary_color", e.target.value)}
                    className="h-10 w-16 rounded border border-ink-200 dark:border-ink-700 bg-transparent"
                  />
                  <Input
                    value={form.primary_color ?? ""}
                    onChange={(e) => setField("primary_color", e.target.value)}
                    placeholder="#2563EB"
                    pattern="^#[0-9A-Fa-f]{6}$"
                    className="font-mono"
                  />
                </div>
              </div>

              <div>
                <Label>Logo</Label>
                <div className="mt-1 flex gap-2 items-center">
                  {form.logo_url && (
                    <img src={form.logo_url} alt="logo" className="h-10 w-10 rounded border border-ink-200" />
                  )}
                  <label className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-ink-200 dark:border-ink-700 cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-800 text-sm">
                    <Upload size={14} /> {uploadLogo.isPending ? "Subiendo…" : "Subir imagen"}
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/svg+xml,image/webp"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadLogo.mutate(f);
                      }}
                    />
                  </label>
                  {form.logo_url && (
                    <button
                      type="button"
                      onClick={() => setField("logo_url", null)}
                      className="text-xs text-bad hover:underline"
                    >
                      quitar
                    </button>
                  )}
                </div>
              </div>

              <div>
                <Label>Favicon</Label>
                <div className="mt-1 flex gap-2 items-center">
                  {form.favicon_url && (
                    <img src={form.favicon_url} alt="favicon" className="h-8 w-8 rounded border border-ink-200" />
                  )}
                  <label className="inline-flex items-center gap-1 px-3 py-2 rounded-lg border border-ink-200 dark:border-ink-700 cursor-pointer hover:bg-ink-50 dark:hover:bg-ink-800 text-sm">
                    <Upload size={14} /> {uploadFavicon.isPending ? "Subiendo…" : "Subir imagen"}
                    <input
                      type="file"
                      accept="image/png,image/x-icon,image/svg+xml"
                      className="hidden"
                      onChange={(e) => {
                        const f = e.target.files?.[0];
                        if (f) uploadFavicon.mutate(f);
                      }}
                    />
                  </label>
                  {form.favicon_url && (
                    <button
                      type="button"
                      onClick={() => setField("favicon_url", null)}
                      className="text-xs text-bad hover:underline"
                    >
                      quitar
                    </button>
                  )}
                </div>
              </div>

              <div className="border-t border-ink-200 dark:border-ink-800 pt-3">
                <Label>Nombre remitente emails</Label>
                <Input
                  className="mt-1"
                  value={form.email_from_name ?? ""}
                  onChange={(e) => setField("email_from_name", e.target.value)}
                  placeholder="Default: usar brand_name"
                />
              </div>

              <div>
                <Label>HTML footer en emails</Label>
                <textarea
                  className="mt-1 w-full min-h-[80px] rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 px-3 py-2 text-sm font-mono"
                  value={form.email_footer_html ?? ""}
                  onChange={(e) => setField("email_footer_html", e.target.value)}
                  placeholder='<hr><p style="color:#888">Sent by ACME Corp</p>'
                />
              </div>

              <div>
                <Label>Email de soporte</Label>
                <Input
                  className="mt-1"
                  type="email"
                  value={form.support_email ?? ""}
                  onChange={(e) => setField("support_email", e.target.value)}
                  placeholder="soporte@tuempresa.com"
                />
              </div>

              <div className="flex justify-end pt-2">
                <Button type="submit" disabled={patch.isPending}>
                  <Save size={14} /> {patch.isPending ? "Guardando…" : "Guardar"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        <Card className="self-start">
          <CardHeader>
            <CardTitle>Preview sidebar</CardTitle>
            <CardDescription>Cómo verán tu plataforma los tenants.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-lg border border-ink-200 dark:border-ink-700 bg-white dark:bg-ink-900 p-4">
              <div className="flex items-center gap-2 pb-3 border-b border-ink-200 dark:border-ink-800">
                {form.logo_url ? (
                  <img src={form.logo_url} alt="" className="w-8 h-8 rounded" />
                ) : (
                  <div className="w-8 h-8 rounded" style={{ background: form.primary_color ?? "#2563EB" }} />
                )}
                <span className="font-semibold">{form.brand_name || "TrendMonitor"}</span>
              </div>
              <div className="mt-3 space-y-2 text-sm text-ink-500">
                <div className="h-8 rounded px-3 flex items-center" style={{ background: `${form.primary_color ?? "#2563EB"}20`, color: form.primary_color ?? "#2563EB" }}>
                  Inicio (item activo)
                </div>
                <div className="h-8 rounded px-3 flex items-center">Monitorización</div>
                <div className="h-8 rounded px-3 flex items-center">Responder</div>
              </div>
            </div>
            <p className="text-[11px] text-ink-500 mt-3">
              Última actualización:{" "}
              {settings.data?.updated_at ? new Date(settings.data.updated_at).toLocaleString("es-PE") : "—"}
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
